/** Content of the tainan package: object handlers and NPC talk. Registered through content/registry.ts. */
import { apply, burst, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say } from '../ui/dialog';
import { stamp } from './passport';
import { registerObjects, registerTalk } from './registry';
import { order, quiz, type FoodSpec } from './trips';

const tn = () => m().tainan;
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];

/** A fact that leads into a `quiz`; `reward` runs once per day on a right answer. */
function factQuiz(id: string, icon: string, text: string, act: string, t: { q: string; a: readonly string[]; right: string; wrong: string }, reward: () => void): void {
  say({
    who: objName(id),
    icon,
    text,
    choices: [{ text: act, run: () => quiz(objName(id), icon, text, t.q, t.a, t.right, t.wrong, () => onceToday(`tn.${id}`) && reward()) }, { text: m().common.back }],
  });
}

const sight = (id: string, icon: string, key: 'chihkan' | 'turtles' | 'confucius' | 'zeelandia' | 'treehouse', act: 'learn' | 'count', stampId: string) => () => {
  const t = tn()[key] as { text: string; learn?: string; count?: string; q: string; a: readonly string[]; right: string; wrong: string };
  factQuiz(id, icon, t.text, (act === 'count' ? t.count : t.learn) ?? '', t, () => {
    apply({ study: 6, stars: 3, mood: 6 });
    stamp(stampId);
  });
};

/** Beef soup is breakfast: the pot runs out at two. */
function beefSoup(): void {
  if (S().time >= 14 * 60) return say({ who: objName('tn_beefSoup'), icon: '🍲', text: tn().closedMorning });
  order('tn_beefSoup', '🍲', tn().beefSoup, { energy: 18, mood: 8 }, () => stamp('tainan'));
}

function lanterns(): void {
  const t = tn().lanterns;
  const h = S().time / 60;
  if (h < 15) return say({ who: objName('tn_lanterns'), icon: '🏮', text: `${t.text}\n\n${t.closed}` });
  factQuiz('tn_lanterns', '🏮', t.text, t.paint, t, () => {
    burst('sparkle', 8);
    apply({ study: 5, stars: 3, mood: 10 });
  });
}

function boatRide(): void {
  const t = tn().boat;
  const h = S().time / 60;
  if (h < 9 || h >= 18) return say({ who: objName('tn_boatRide'), icon: '🚤', text: `${t.text}\n\n${t.closed}` });
  say({
    who: objName('tn_boatRide'),
    icon: '🚤',
    text: t.text,
    choices: [
      {
        text: t.ride,
        cost: m().world3.market.cost(t.price),
        run: () => {
          if (S().coins < t.price) return toast(t.poor);
          quiz(objName('tn_boatRide'), '🚤', t.text, t.q, t.a, t.right, t.wrong, () => {
            sfx('water');
            burst('sparkle', 6);
            apply({ coins: -t.price, mood: 16, energy: 4, minutes: 30 });
            if (onceToday('tn.boat')) apply({ stars: 3 }, { float: false });
            stamp('anping');
          });
        },
      },
      { text: m().common.back },
    ],
  });
}

const food = (id: 'tn_danzai' | 'tn_coffin' | 'tn_shrimp' | 'tn_candied', icon: string, spec: () => FoodSpec, fx: { energy?: number; mood?: number }) => () => order(id, icon, spec(), fx, () => stamp(id === 'tn_shrimp' || id === 'tn_candied' ? 'anping' : 'tainan'));

registerObjects({
  tn_shuttle() {
    const t = tn().shuttle;
    factQuiz('tn_shuttle', '🚌', t.text, t.ask, t, () => apply({ study: 4, stars: 1 }));
  },
  tn_chihkan: sight('tn_chihkan', '🏯', 'chihkan', 'learn', 'tainan'),
  tn_turtles: sight('tn_turtles', '🐢', 'turtles', 'count', 'tainan'),
  tn_confucius: sight('tn_confucius', '🛕', 'confucius', 'learn', 'tainan'),
  tn_zeelandia: sight('tn_zeelandia', '🏰', 'zeelandia', 'learn', 'anping'),
  tn_treehouse: sight('tn_treehouse', '🌳', 'treehouse', 'learn', 'anping'),
  tn_lanterns: lanterns,
  tn_boatRide: boatRide,
  tn_beefSoup: beefSoup,
  tn_danzai: food('tn_danzai', '🍜', () => tn().danzai, { energy: 16, mood: 8 }),
  tn_coffin: food('tn_coffin', '🍞', () => tn().coffin, { energy: 18, mood: 10 }),
  tn_shrimp: food('tn_shrimp', '🍘', () => tn().shrimp, { energy: 8, mood: 10 }),
  tn_candied: food('tn_candied', '🍬', () => tn().candied, { energy: 6, mood: 12 }),
});

/** NPC talk: vendors offer their food, the rest share a line (some are English practice). */
registerTalk((id) => {
  if (!id.startsWith('tn_')) return false;
  const t = tn().talk;
  const lines = (t as unknown as Record<string, readonly string[] | undefined>)[id];
  if (!Array.isArray(lines)) return false;
  const icons: Record<string, string> = {
    tn_soupGranny: '🍲', tn_fortGuide: '🏰', tn_scholar: '📜', tn_crackerLady: '🍘', tn_candiedAunt: '🍬', tn_fisher: '🎣',
    tn_kid1: '🪁', tn_kid2: '✏️', tn_lanternArtist: '🏮', tn_shuttleDriver: '🚌', tn_cyclist: '🚲', tn_grandpa: '♟️', tn_boatman: '🚤',
  };
  const line = pick(lines).replace(/^English practice:\s*/, '').replace(/\s*\(English practice\)$/, '');
  // Speak the quoted English phrases (in every language the quotes stay in English).
  const quoted = [...line.matchAll(/“([^”]+)”/g)].map((q) => q[1]).filter((q) => /^[\x20-\x7e’…]+$/.test(q));
  const en = quoted.length ? quoted.join(' … ') : undefined;
  if (en) sayEnglish(en, 0.9);
  const offers: Partial<Record<string, { text: string; run: () => void }>> = {
    tn_soupGranny: { text: `🍲 ${objName('tn_beefSoup')}`, run: beefSoup },
    tn_crackerLady: { text: `🍘 ${objName('tn_shrimp')}`, run: food('tn_shrimp', '🍘', () => tn().shrimp, { energy: 8, mood: 10 }) },
    tn_candiedAunt: { text: `🍬 ${objName('tn_candied')}`, run: food('tn_candied', '🍬', () => tn().candied, { energy: 6, mood: 12 }) },
    tn_boatman: { text: `🚤 ${objName('tn_boatRide')}`, run: boatRide },
    tn_lanternArtist: { text: `🏮 ${objName('tn_lanterns')}`, run: lanterns },
  };
  const offer = offers[id];
  say({ who: npcName(id), icon: icons[id] ?? '💬', text: line, en, choices: offer ? [offer, { text: m().common.back }] : undefined });
  return true;
});
